// src/AdminDashboard/components/MultiStepGameForm/steps/IntroStep.js
import React from "react";
import {
  getTimezoneInfo,
  getTimezoneAbbreviation,
} from "../../../../utils/datetime";
import ReactQuill from "react-quill";
import {
  GAME_TYPE_FORM_CONFIG,
  getAllowedGameTypes,
} from "../config/gameTypeFormConfig";
import "react-quill/dist/quill.snow.css";

const IntroStep = ({
  formData,
  updateFormData,
  fieldErrors = {},
  gameAccess = ["all"],
  hideTypeSelector = false,
  hideGameSpecificInputs = false,
}) => {
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ align: [] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link"],
      [{ color: [] }, { background: [] }],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "align",
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "indent",
    "link",
    "color",
    "background",
  ];

  const isQuillEmpty = (content) =>
    !content || content === "<p><br></p>" || content.trim() === "";

  const applyDefaultCenterAlign = (content, editor) => {
    if (!editor || !isQuillEmpty(content)) return;
    editor.format("align", "center", "user");
  };

  const getNowLocalDateTime = () => {
    try {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const min = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    } catch (_) {
      return "";
    }
  };

  const tzInfo = getTimezoneInfo();
  const tzAbbrev = getTimezoneAbbreviation();
  const allowedTypes = getAllowedGameTypes(gameAccess);
  const availableTypes = allowedTypes.map((type) => ({
    value: type,
    label: GAME_TYPE_FORM_CONFIG[type]?.label || type,
  }));

  const normalizeHex = (value) => {
    if (!value) return "";
    const v = String(value).trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
    if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`;
    const m3 = v.match(/^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/);
    if (m3) return `#${m3[1]}${m3[1]}${m3[2]}${m3[2]}${m3[3]}${m3[3]}`;
    return v;
  };

  return (
    <div className="step-content">
      <div className="form-group">
        <label>GAME NAME</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData("name", e.target.value)}
          placeholder="Game Title"
          required
          className={fieldErrors.name ? "error" : ""}
        />
        {fieldErrors.name && (
          <div className="field-error">{fieldErrors.name}</div>
        )}
      </div>

      {!hideTypeSelector && (
        <div className="form-group">
          <label>GAME TYPE</label>
          <select
            value={formData.type || ""}
            onChange={(e) => updateFormData("type", e.target.value)}
          >
            <option value="" disabled>
              Select game type
            </option>
            {availableTypes.map((typeOption) => (
              <option key={typeOption.value} value={typeOption.value}>
                {typeOption.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {!hideGameSpecificInputs && (
        <>
          <div className="form-group">
            <label>ADMIN NOTES</label>
            <textarea
              value={formData.adminNotes || ""}
              onChange={(e) => updateFormData("adminNotes", e.target.value)}
              rows="2"
              placeholder="Internal notes (not shown to players)"
              className="admin-notes"
            />
          </div>

          <div
            className="timezone-hint"
            style={{
              fontSize: "0.8rem",
              color: "#666",
              margin: "-0.5rem 0 0.75rem",
            }}
          >
            *Times are shown in your local timezone: {tzInfo.tz} {tzInfo.offset} (
            {tzAbbrev})
          </div>

          <div className="form-group">
            <label>SCHEDULE GAME ACTIVE FROM</label>
            <input
              type="datetime-local"
              value={formData.activeFrom}
              onChange={(e) => updateFormData("activeFrom", e.target.value)}
              min={getNowLocalDateTime()}
            />
          </div>

          <div className="form-group">
            <label>SCHEDULE GAME ACTIVE UNTIL</label>
            <input
              type="datetime-local"
              value={formData.activeUntil}
              onChange={(e) => updateFormData("activeUntil", e.target.value)}
              min={getNowLocalDateTime()}
            />
            {fieldErrors.activeWindow && (
              <div className="field-error">{fieldErrors.activeWindow}</div>
            )}
          </div>

          <div className="form-group">
            <label>WELCOME MESSAGE</label>
            <textarea
              value={formData.introMessage.instructions}
              onChange={(e) =>
                updateFormData("introMessage", {
                  ...formData.introMessage,
                  instructions: e.target.value,
                })
              }
              rows="4"
              placeholder="You will be shown cards and must complete the game objective."
            />
          </div>

          <div className="form-group">
            <label>HOW TO INSTRUCTIONS</label>
            <div className="rich-text-note">*Supports rich text formatting</div>
            <ReactQuill
              theme="snow"
              value={formData.introMessage.howTo || ""}
              onChange={(content) =>
                updateFormData("introMessage", {
                  ...formData.introMessage,
                  howTo: content,
                })
              }
              modules={quillModules}
              formats={quillFormats}
              onFocus={(_, __, editor) =>
                applyDefaultCenterAlign(formData.introMessage.howTo, editor)
              }
              placeholder="Add gameplay instructions for this game type."
            />
          </div>

          <div className="form-group">
            <label>
              TERMS & CONDITIONS <em>OPTIONAL</em>
            </label>
            <div className="rich-text-note">*Shown on separate page</div>

            <div
              className="toggle-group"
              style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem" }}
            >
              <label
                style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
              >
                <input
                  type="radio"
                  name="termsType"
                  value="none"
                  checked={formData.termsType === "none"}
                  onChange={() => {
                    updateFormData("termsType", "none");
                    updateFormData("terms", "");
                  }}
                />
                None
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
              >
                <input
                  type="radio"
                  name="termsType"
                  value="url"
                  checked={formData.termsType === "url"}
                  onChange={() => {
                    updateFormData("termsType", "url");
                    if (formData.terms && !/^https?:\/\//i.test(formData.terms)) {
                      updateFormData("terms", "");
                    }
                  }}
                />
                URL
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
              >
                <input
                  type="radio"
                  name="termsType"
                  value="text"
                  checked={formData.termsType === "text"}
                  onChange={() => {
                    updateFormData("termsType", "text");
                    if (/^https?:\/\//i.test(formData.terms || "")) {
                      updateFormData("terms", "");
                    }
                  }}
                />
                Text
              </label>
            </div>

            {formData.termsType === "url" && (
              <>
                <input
                  type="url"
                  value={formData.terms}
                  onChange={(e) => updateFormData("terms", e.target.value)}
                  onBlur={(e) => {
                    const raw = (e.target.value || "").trim();
                    if (raw && !/^https?:\/\//i.test(raw)) {
                      updateFormData("terms", `https://${raw}`);
                    }
                  }}
                  placeholder="https://example.com/terms"
                  className={`url-input ${fieldErrors.terms ? "error" : ""}`}
                />
                {fieldErrors.terms && (
                  <div className="field-error">{fieldErrors.terms}</div>
                )}
              </>
            )}

            {formData.termsType === "text" && (
              <ReactQuill
                theme="snow"
                value={formData.terms}
                onChange={(content) => updateFormData("terms", content)}
                modules={quillModules}
                formats={quillFormats}
                onFocus={(_, __, editor) =>
                  applyDefaultCenterAlign(formData.terms, editor)
                }
                placeholder="Enter terms and conditions..."
              />
            )}
          </div>

          <div className="colors-intro">
            <p>All colors are in hex codes</p>
          </div>

          <div className="form-group">
            <label>MAIN TEXT COLOR</label>
            <div className="color-input-group">
              <input
                type="color"
                value={formData.textColor}
                onChange={(e) => updateFormData("textColor", e.target.value)}
                className="color-picker"
              />
              <input
                type="text"
                value={formData.textColor}
                onChange={(e) => updateFormData("textColor", e.target.value.trim())}
                onBlur={(e) =>
                  updateFormData("textColor", normalizeHex(e.target.value))
                }
                placeholder="#000000"
                className="hex-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>MAIN BUTTON COLOR</label>
            <div className="color-input-group">
              <input
                type="color"
                value={formData.buttonColor}
                onChange={(e) => updateFormData("buttonColor", e.target.value)}
                className="color-picker"
              />
              <input
                type="text"
                value={formData.buttonColor}
                onChange={(e) =>
                  updateFormData("buttonColor", e.target.value.trim())
                }
                onBlur={(e) =>
                  updateFormData("buttonColor", normalizeHex(e.target.value))
                }
                placeholder="#000000"
                className="hex-input"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IntroStep;
